import React, { createContext, useRef, useEffect, useState, useMemo, } from 'react';
import cookies from 'browser-cookies';
import { authorizerTokenCookie, authorizerTokenCookieExpirationDate, forgeAccessTokenExpiration, forgeRefreshTokenCookie, forgeRevalidationCookie, } from '../types/authenticationContext.types';
import { Events } from '../utils/sharedWorkers.types';
import { DateTime } from 'luxon';
import { useLogoutContext } from '../contexts/AuthenticationWrapper/LogoutContext';
import { createHash } from 'crypto';
import { v4 as uuidv4 } from 'uuid';
function hash(string) {
    return createHash('sha256').update(string).digest('hex');
}
const ReauthSharedWorkerRefContext = createContext({
    workerRef: { current: null },
    modalOpen: false,
    expirationTime: 0,
    openModal: () => void 0,
    initializeExtendSession: () => void 0,
    dispatchCloseSession: () => void 0,
});
export const useReauthSharedWorkerProvider = () => React.useContext(ReauthSharedWorkerRefContext);
export default function SharedWorkerProvider({ children, workerUrl = '/workers/forge.sharedworker.mjs', workerName = 'forgeWorker', }) {
    const workerRef = useRef(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [expirationTime, setExpirationTime] = useState(1000);
    const { logout } = useLogoutContext();
    const adpSessionId = uuidv4();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const postMessage = (type, data) => {
        workerRef.current?.port.postMessage({ type, data });
    };
    useEffect(() => {
        if (workerUrl) {
            workerRef.current = new SharedWorker(workerUrl, {
                type: 'module',
                name: workerName,
            });
        }
        if (workerRef.current) {
            workerRef?.current?.port.start();
            const handleMessage = event => {
                const { data = {}, type } = event.data;
                console.warn('Shared Worker response: ', {
                    type,
                    data: {
                        ...data,
                        ...(data?.access_token && {
                            access_token: hash(data?.access_token),
                        }),
                        ...(data?.accessToken && {
                            accessToken: hash(data?.accessToken),
                        }),
                        ...(data?.refresh_token && {
                            refresh_token: hash(data?.refresh_token),
                        }),
                        ...(data?.refreshToken && {
                            refreshToken: hash(data?.refreshToken),
                        }),
                    },
                });
                if (type) {
                    let expiresTimeInSeconds = 120;
                    let expirationDate = '';
                    switch (type) {
                        case Events.PROPAGATE_FORGE_TOKEN_UPDATED:
                            expiresTimeInSeconds =
                                data.expires_in ?? expiresTimeInSeconds;
                            expirationDate = DateTime.now()
                                .plus({ seconds: +(expiresTimeInSeconds || 0) })
                                .toISO();
                            cookies.set(authorizerTokenCookie, data.access_token);
                            cookies.set(forgeRefreshTokenCookie, data.refresh_token);
                            cookies.set(authorizerTokenCookieExpirationDate, expirationDate);
                            cookies.set(forgeAccessTokenExpiration, expirationDate);
                            break;
                        case Events.PROPAGATE_SESSION_FINISH_TIME:
                            expiresTimeInSeconds =
                                data.expires_in ?? expiresTimeInSeconds;
                            expirationDate = DateTime.now()
                                .plus({
                                seconds: +expiresTimeInSeconds,
                            })
                                .toISO();
                            break;
                        case Events.PROPAGATE_OPEN_MODAL:
                            setModalOpen(true);
                            setExpirationTime(data);
                            break;
                        case Events.PROPAGATE_LOGOUT:
                            logout.current?.();
                            break;
                        case Events.PROPAGATE_CLOSE_MODAL:
                            setModalOpen(false);
                            break;
                        default:
                            break;
                    }
                }
            };
            workerRef?.current?.port.addEventListener('message', handleMessage);
            return () => {
                workerRef?.current?.port.removeEventListener('message', handleMessage);
                workerRef?.current?.port.close();
            };
        }
    }, [workerUrl]);
    const initData = () => {
        if (workerRef.current) {
            if (cookies.get(forgeRefreshTokenCookie)) {
                const payload = {
                    accessToken: cookies.get(authorizerTokenCookie),
                    refresh_token: cookies.get(forgeRefreshTokenCookie),
                    refreshExpirationDate: cookies.get(forgeRevalidationCookie),
                    finalExpirationDate: '',
                    expirationDate: cookies.get(forgeAccessTokenExpiration),
                    revalidationDate: cookies.get(forgeRevalidationCookie),
                };
                postMessage(Events.INIT_FORGE_DATA, payload);
            }
        }
    };
    useEffect(() => {
        const timeout = setTimeout(() => initData(), 1000);
        return () => clearTimeout(timeout);
    }, []);
    useEffect(() => {
        if (workerRef?.current?.port) {
            setTimeout(() => {
                const payload = {
                    accessToken: cookies.get(authorizerTokenCookie),
                    expirationDate: cookies.get(authorizerTokenCookieExpirationDate),
                    finalExpirationDate: '',
                    redirectUri: window.location.origin,
                };
                postMessage(Events.INIT_AUTH0_DATA, payload);
            }, 1000);
        }
    }, []);
    const openModal = () => {
        postMessage(Events.PROPAGATE_OPEN_MODAL);
    };
    const initializeExtendSession = () => {
        const payload = {};
        const expirationDate = cookies.get(authorizerTokenCookieExpirationDate);
        if (expirationDate)
            payload.expirationDate = expirationDate;
        postMessage(Events.INIT_EXTEND_SESSION, payload);
    };
    const dispatchCloseSession = () => postMessage(Events.DO_LOGOUT);
    const contextValue = useMemo(() => ({
        workerRef,
        modalOpen,
        expirationTime,
        openModal,
        initializeExtendSession,
        dispatchCloseSession,
    }), [
        workerRef,
        modalOpen,
        expirationTime,
        openModal,
        initializeExtendSession,
        dispatchCloseSession,
    ]);
    React.useEffect(() => {
        console.log('Shared Worker Provider mounted');
    }, []);
    return (React.createElement(ReauthSharedWorkerRefContext.Provider, { value: contextValue }, children));
}
